import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";

import { createPerson } from "../../../data/actions/personActions";
import PersonApi from "../../../data/api/personsApi";
import { IState } from "../../../data/reducers/state";
import { IAssociation } from "../../../types/association/Association";
import { BasketballGrade } from "../../../types/common/basketballGrades";
import { StatisticianGrade } from "../../../types/common/statisticianGrade";
import {
  IFuzzyTally,
} from "../../../types/imports/ImportPerson";
import {
  IPerson,
  IPersonAssociation,
  PersonAssociationTenure,
} from "../../../types/persons/person";
import { Privilege } from "../../../types/persons/privilege";
import { Role } from "../../../types/persons/Role";
import { CenteredLoader } from "../../common/loader";
import PrivilegeForm from "../../management/generic/PrivilegeForm";
import RoleForm from "../../management/generic/RoleForm";
import FuzzyPersonMatch from "./FuzzyPersonmatch";
import PersonAssociationModal from "./PersonAssociationModal";

type ExpressAssociationState = IPersonAssociation & {
  adminPayrate?: number | null;
};

interface ExpressFormState {
  firstName: string;
  lastName: string;
  preferredName: string;
  email: string;
  mobile: string;
  association: ExpressAssociationState;
  referee: {
    grade: number | null;
    plus: boolean;
    isInterestedInPromotion: boolean;
  };
  scoretable: {
    grade: number | null;
  };
  refereeCoach: {
    grade: number | null;
  };
  statistician: {
    grade: number | null;
    fibaAccredited: boolean;
  };
}

type FuzzyState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "none" }
  | { status: "match"; tally: IFuzzyTally<IPerson> }
  | { status: "error"; message: string };

interface StateProps {
  associations: IAssociation[];
}

interface DispatchProps {
  createPerson: (person: Partial<IPerson>) => Promise<IPerson>;
}

interface OwnProps {
  defaultAssociationId?: string;
  onCancel: () => void;
  onComplete: (personId: string) => void;
  onOpenExisting: (personId: string) => void;
}

type PersonExpressFormProps = StateProps & DispatchProps & OwnProps;

const buildInitialAssociation = (
  associationId?: string,
): ExpressAssociationState => ({
  associationId: associationId || "",
  privilege: Privilege.none,
  role: Role.referee,
  start: new Date(),
  finish: null,
  tenure: "permanent",
  adminPayrate: null,
});

const buildInitialForm = (associationId?: string): ExpressFormState => ({
  firstName: "",
  lastName: "",
  preferredName: "",
  email: "",
  mobile: "",
  association: buildInitialAssociation(associationId),
  referee: {
    grade: null,
    plus: false,
    isInterestedInPromotion: false,
  },
  scoretable: {
    grade: null,
  },
  refereeCoach: {
    grade: null,
  },
  statistician: {
    grade: null,
    fibaAccredited: false,
  },
});

const PersonExpressForm: React.FC<PersonExpressFormProps> = ({
  associations,
  createPerson,
  defaultAssociationId,
  onCancel,
  onComplete,
  onOpenExisting,
}) => {
  const [form, setForm] = useState<ExpressFormState>(() =>
    buildInitialForm(defaultAssociationId),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fuzzyState, setFuzzyState] = useState<FuzzyState>({ status: "idle" });
  const lastFuzzyKeyRef = useRef<string>("");
  const fuzzyAbortRef = useRef<AbortController | null>(null);
  const [associationModalState, setAssociationModalState] = useState<{
    personId: string;
    defaults: IPersonAssociation;
  } | null>(null);

  const associationOptions = useMemo(
    () =>
      associations.map((association) => ({
        value: association._id,
        label: association.name,
      })),
    [associations],
  );

  const [dFirstName] = useDebouncedValue(form.firstName, 400);
  const [dLastName] = useDebouncedValue(form.lastName, 400);
  const [dEmail] = useDebouncedValue(form.email, 400);
  const [dMobile] = useDebouncedValue(form.mobile, 400);

  useEffect(() => {
    const minName = dFirstName.trim().length >= 2 && dLastName.trim().length >= 2;
    const minContact =
      dEmail.trim().length >= 5 || dMobile.replace(/\D/g, "").length >= 6;

    if (!minName && !minContact) {
      if (fuzzyAbortRef.current) {
        fuzzyAbortRef.current.abort();
        fuzzyAbortRef.current = null;
      }
      lastFuzzyKeyRef.current = "";
      if (fuzzyState.status !== "idle") {
        setFuzzyState({ status: "idle" });
      }
      return undefined;
    }

    const key = JSON.stringify([
      dFirstName.trim().toLowerCase(),
      dLastName.trim().toLowerCase(),
      dEmail.trim().toLowerCase(),
      dMobile.trim(),
    ]);

    if (key === lastFuzzyKeyRef.current) return undefined;

    lastFuzzyKeyRef.current = key;

    if (fuzzyAbortRef.current) {
      fuzzyAbortRef.current.abort();
      fuzzyAbortRef.current = null;
    }

    setFuzzyState({ status: "loading" });

    const controller = new AbortController();
    fuzzyAbortRef.current = controller;

    const payload: Partial<IPerson> = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
    };

    PersonApi.fuzzyTally(payload, { signal: controller.signal })
      .then((tally) => {
        if (controller.signal.aborted) return;
        fuzzyAbortRef.current = null;
        if (tally) {
          setFuzzyState({ status: "match", tally });
          return;
        }
        setFuzzyState({ status: "none" });
      })
      .catch((err: any) => {
        if (controller.signal.aborted) return;
        fuzzyAbortRef.current = null;
        const errorCode = err?.code;
        if (errorCode === "ERR_CANCELED") return;
        console.error("Fuzzy match failed", err);
        setFuzzyState({
          status: "error",
          message: err?.message || "Unable to check for duplicates right now.",
        });
      });

    return () => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
      if (fuzzyAbortRef.current === controller) {
        fuzzyAbortRef.current = null;
      }
    };
  }, [
    dEmail,
    dFirstName,
    dLastName,
    dMobile,
    form.email,
    form.firstName,
    form.lastName,
    form.mobile,
  ]);

  const roleIncludes = (role: Role) => Role.hasRole(form.association.role, role);

  const canSubmit =
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    form.association.associationId.length > 0 &&
    form.association.role !== 0 &&
    !isSaving;

  const updateAssociation = <K extends keyof ExpressAssociationState>(
    key: K,
    value: ExpressAssociationState[K],
  ) => {
    setForm((prev) => ({
      ...prev,
      association: {
        ...prev.association,
        [key]: value,
      },
    }));
  };

  const gradeOptions = useMemo(
    () =>
      BasketballGrade.all().map((grade) => ({
        value: grade.toString(),
        label: BasketballGrade.toString(grade),
      })),
    [],
  );

  const scoretableOptions = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, index) => ({
        value: index.toString(),
        label: index === 0 ? "Ungraded" : `Level ${index}`,
      })),
    [],
  );

  const statisticianOptions = useMemo(
    () =>
      StatisticianGrade.all().map((grade) => ({
        value: grade.toString(),
        label: grade === StatisticianGrade.level0 ? "Ungraded" : `Level ${grade}`,
      })),
    [],
  );

  const resetForm = () => {
    setForm(buildInitialForm(defaultAssociationId));
    setError(null);
    setFuzzyState({ status: "idle" });
    lastFuzzyKeyRef.current = "";
    if (fuzzyAbortRef.current) {
      fuzzyAbortRef.current.abort();
      fuzzyAbortRef.current = null;
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    setError(null);

    const association: IPersonAssociation = {
      associationId: form.association.associationId,
      privilege: form.association.privilege,
      role: form.association.role,
      start: form.association.start,
      finish: null,
      tenure: form.association.tenure,
      adminPayrate: form.association.adminPayrate ?? undefined,
    };

    const payload: Partial<IPerson> = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      preferredName: form.preferredName.trim() || undefined,
      email: form.email.trim() || undefined,
      mobile: form.mobile.trim() || undefined,
      associations: [association],
      referee: roleIncludes(Role.referee)
        ? {
          grade: form.referee.grade ?? undefined,
          plus: form.referee.plus,
          isInterestedInPromotion: form.referee.isInterestedInPromotion,
        }
        : undefined,
      scoretable: roleIncludes(Role.scoretable)
        ? {
          grade: form.scoretable.grade ?? undefined,
        }
        : undefined,
      refereeCoach: roleIncludes(Role.refereeCoach)
        ? {
          grade: form.refereeCoach.grade ?? undefined,
        }
        : undefined,
      statistician: roleIncludes(Role.statistician)
        ? {
          grade: form.statistician.grade ?? undefined,
          fibaAccredited: form.statistician.fibaAccredited,
        }
        : undefined,
    };

    try {
      const created = await createPerson(payload);
      onComplete(created._id);
      resetForm();
    } catch (err: any) {
      console.error("Failed to create person", err);
      setError(err?.message || "Unable to create person");
    } finally {
      setIsSaving(false);
    }
  };

  const openAssociationModalForExisting = () => {
    if (fuzzyState.status !== "match") return;
    const existing = fuzzyState.tally.d;
    if (!existing?._id) return;
    if (!form.association.associationId) {
      setError('Select an association before linking to an existing person.');
      return;
    }

    setAssociationModalState({
      personId: existing._id,
      defaults: {
        associationId: form.association.associationId,
        privilege: form.association.privilege,
        role: form.association.role,
        start: form.association.start,
        finish: null,
        tenure: form.association.tenure || "permanent",
        adminPayrate: form.association.adminPayrate ?? undefined,
      },
    });
  };

  const handleAssociationModalSave = async (association: IPersonAssociation) => {
    if (!associationModalState) return;

    setIsSaving(true);
    setError(null);

    try {
      await PersonApi.associatePerson(associationModalState.personId, association);
      onComplete(associationModalState.personId);
      setAssociationModalState(null);
      setFuzzyState({ status: "idle" });
    } catch (err: any) {
      console.error("Failed to associate existing person", err);
      setError(err?.message || "Unable to associate existing person");
    } finally {
      setIsSaving(false);
    }
  };

  const duplicateBanner = (() => {
    switch (fuzzyState.status) {
      case "idle":
        return null;
      case "loading":
        return (
          <Alert color="blue" icon={<Loader size="sm" />}>
            Checking for existing matches…
          </Alert>
        );
      case "none":
        return <Alert color="green">No close matches detected.</Alert>;
      case "error":
        return <Alert color="red">{fuzzyState.message}</Alert>;
      case "match":
        return (
          <Card withBorder padding="md" radius="md">
            <Stack gap="sm">
              <FuzzyPersonMatch
                fuzzyTally={fuzzyState.tally}
                onAssociate={openAssociationModalForExisting}
              />
            </Stack>
          </Card>
        );
      default:
        return null;
    }
  })();

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text size="lg" fw={600}>
          Express person creation
        </Text>
        <Text size="sm" c="dimmed">
          Capture identity, association, and role information on a single screen with live duplicate warnings.
        </Text>
      </Stack>

      {duplicateBanner}

      <Card withBorder padding="md" radius="md">
        <Stack gap="md">
          <Text fw={500}>Identity</Text>
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            <TextInput
              label="First name"
              value={form.firstName}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, firstName: event.currentTarget.value }))
              }
              required
            />
            <TextInput
              label="Last name"
              value={form.lastName}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, lastName: event.currentTarget.value }))
              }
              required
            />
            <TextInput
              label="Preferred name"
              value={form.preferredName}
              placeholder={form.firstName || undefined}
              onChange={(event) => {
                setForm((prev) => ({
                  ...prev,
                  preferredName: event.currentTarget.value,
                }));
              }}
            />
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
            <TextInput
              label="Email"
              value={form.email}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, email: event.currentTarget.value }))
              }
              placeholder="jane@example.com"
            />
            <TextInput
              label="Mobile"
              value={form.mobile}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, mobile: event.currentTarget.value }))
              }
              placeholder="0400 000 000"
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="md" radius="md">
        <Stack gap="md">
          <Text fw={500}>Association & enrolment</Text>
          <Select
            label="Association"
            data={associationOptions}
            value={form.association.associationId}
            onChange={(value) =>
              updateAssociation("associationId", value ? value : "")
            }
            placeholder="Select association"
            required
          />
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Roles
              </Text>
              <RoleForm
                role={form.association.role}
                onChange={(value: Role) => updateAssociation("role", value)}
              />
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Privileges
              </Text>
              <PrivilegeForm
                privilege={form.association.privilege}
                onChange={(value: Privilege) =>
                  updateAssociation("privilege", value)
                }
              />
            </Stack>
          </SimpleGrid>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="md">
            <DateInput
              label="Start date"
              value={form.association.start}
              onChange={(value) =>
                updateAssociation("start", value || new Date())
              }
            />
            <Select
              label="Tenure"
              value={(form.association.tenure || "permanent") as PersonAssociationTenure}
              onChange={(value) =>
                updateAssociation(
                  "tenure",
                  (value as PersonAssociationTenure) || "permanent",
                )
              }
              data={[
                { label: "Permanent", value: "permanent" },
                { label: "Temporary", value: "temporary" },
                { label: "Elite", value: "elite" },
                { label: "Casual", value: "casual" },
                { label: "Affiliate", value: "affiliate" },
              ]}
            />
            <NumberInput
              label="Admin payrate override"
              leftSection="$"
              min={0}
              step={0.25}
              allowNegative={false}
              value={form.association.adminPayrate ?? undefined}
              onChange={(value) =>
                updateAssociation(
                  "adminPayrate",
                  Number.isFinite(value) ? (value as number) : null,
                )
              }
            />
          </SimpleGrid>
        </Stack>
      </Card>

      {roleIncludes(Role.referee) && (
        <Card withBorder padding="md" radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Referee details</Text>
              <Badge color="blue">Enabled</Badge>
            </Group>
            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
              <Select
                label="Grade"
                data={gradeOptions}
                value={
                  form.referee.grade === null
                    ? null
                    : form.referee.grade.toString()
                }
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    referee: {
                      ...prev.referee,
                      grade: value ? Number(value) : null,
                    },
                  }))
                }
                placeholder="Select grade"
              />
              <Checkbox
                label="Plus referee"
                checked={form.referee.plus}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    referee: {
                      ...prev.referee,
                      plus: event.currentTarget.checked,
                    },
                  }))
                }
              />
              <Checkbox
                label="Interested in promotion"
                checked={form.referee.isInterestedInPromotion}
                onChange={(event) =>
                  setForm((prev) => ({
                    ...prev,
                    referee: {
                      ...prev.referee,
                      isInterestedInPromotion: event.currentTarget.checked,
                    },
                  }))
                }
              />
            </SimpleGrid>
          </Stack>
        </Card>
      )}

      {roleIncludes(Role.scoretable) && (
        <Card withBorder padding="md" radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Scoretable details</Text>
              <Badge color="blue">Enabled</Badge>
            </Group>
            <Select
              label="Grade"
              data={scoretableOptions}
              value={
                form.scoretable.grade === null
                  ? null
                  : form.scoretable.grade.toString()
              }
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  scoretable: {
                    grade: value ? Number(value) : null,
                  },
                }))
              }
              placeholder="Select grade"
            />
          </Stack>
        </Card>
      )}

      {roleIncludes(Role.statistician) && (
        <Card withBorder padding="md" radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Statistician details</Text>
              <Badge color="blue">Enabled</Badge>
            </Group>
            <Select
              label="Grade"
              data={statisticianOptions}
              value={
                form.statistician.grade === null
                  ? null
                  : form.statistician.grade.toString()
              }
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  statistician: {
                    ...prev.statistician,
                    grade: value ? Number(value) : null,
                  },
                }))
              }
              placeholder="Select grade"
            />
            <Checkbox
              label="FIBA accredited"
              checked={form.statistician.fibaAccredited}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  statistician: {
                    ...prev.statistician,
                    fibaAccredited: event.currentTarget.checked,
                  },
                }))
              }
            />
          </Stack>
        </Card>
      )}

      {roleIncludes(Role.refereeCoach) && (
        <Card withBorder padding="md" radius="md">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Referee coach details</Text>
              <Badge color="blue">Enabled</Badge>
            </Group>
            <Select
              label="Grade"
              data={scoretableOptions}
              value={
                form.refereeCoach.grade === null
                  ? null
                  : form.refereeCoach.grade.toString()
              }
              onChange={(value) =>
                setForm((prev) => ({
                  ...prev,
                  refereeCoach: {
                    grade: value ? Number(value) : null,
                  },
                }))
              }
              placeholder="Select grade"
            />
          </Stack>
        </Card>
      )}

      <Card withBorder padding="md" radius="md">
        <Stack gap="xs">
          <Text fw={500}>Review</Text>
          <Text size="sm" c="dimmed">
            Double-check details before creating the person.
          </Text>
          <Text fw={600}>
            {form.firstName || "(First name)"} {form.lastName || "(Last name)"}
          </Text>
          {form.email && <Text size="sm">Email: {form.email}</Text>}
          {form.mobile && <Text size="sm">Mobile: {form.mobile}</Text>}
          <Text size="sm">
            Association:
            {" "}
            {associationOptions.find((option) => option.value === form.association.associationId)?.label ||
              (form.association.associationId ? form.association.associationId : "Not selected")}
          </Text>
          <Text size="sm">
            Roles: {Role.getRoleNames(form.association.role).join(", ") || "None"}
          </Text>
          <Text size="sm">
            Privileges: {Privilege.getNames(form.association.privilege).join(", ") || "None"}
          </Text>
        </Stack>
      </Card>

      {error && <Alert color="red">{error}</Alert>}

      <Group justify="space-between">
        <Button
          variant="outline"
          color="gray"
          onClick={() => {
            resetForm();
            onCancel();
          }}
        >
          Cancel
        </Button>
        <Button color="blue" onClick={submit} disabled={!canSubmit} loading={isSaving}>
          Create person
        </Button>
      </Group>
      {isSaving && <CenteredLoader />}
      {associationModalState && (
        <PersonAssociationModal
          personId={associationModalState.personId}
          onCancel={() => setAssociationModalState(null)}
          onSave={handleAssociationModalSave}
          returnUnsaved
          defaultValues={associationModalState.defaults}
          associations={associations}
        />
      )}
    </Stack>
  );
};

function mapStateToProps(state: IState): StateProps {
  return {
    associations: state.associations.admin
      .map((id) => state.associations.map[id])
      .filter(Boolean),
  };
}

export default connect(mapStateToProps, { createPerson })(PersonExpressForm);
